import { Component, OnInit } from '@angular/core';
import { faAddressCard, faEdit, faEnvelope, faUserSlash } from '@fortawesome/pro-duotone-svg-icons';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebBrick } from 'src/app/components/bricks/samweb-brick.decorator';
import { getEntryValueForColumn } from 'src/app/helper/directoryEntryHelper';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { EmailPurpose } from 'src/app/samwebDescription/samwin/EmailPurpose';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { UserStatusEnum } from 'src/app/samwebDescription/samwin/UserStatusEnum';
import { DirectorySearchInGivenColumns } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-browser-behaviour/directory-browser-behaviour.actions';

import { DirectoryDataBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-data/directory-data-behaviour.state';
import { DirectoryBrowserEditEntry } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-editing/directory-editing-behaviour.actions';
import { DirectoryHelperState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-helper/directory-helper.state';
import { DirectoryDetailsSettingState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-details-brick-settings/directory-details-settings.state';
import { DirectorySettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import { StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import { StartEmailProcess } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.actions';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { DirectoryEntryRemoteDto } from 'src/generated/swagger';
import { DirectoryDetailsSettingComponent } from './directory-details-setting/directory-details-setting.component';

@Component({
   selector: 'samweb-directory-details-brick',
   templateUrl: './directory-details-brick.component.html',
   styleUrls: ['./directory-details-brick.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'directoryDetails',
   brickTitleTranslationKey: 'brick.brick_definitions.directory_details.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.directory_details.description',
   brickIcon: faAddressCard,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.GENERAL,
   settingsComponent: DirectoryDetailsSettingComponent,
   settingStateHandler: DirectoryDetailsSettingState,
})
export class DirectoryDetailsBrickComponent extends BasicBrickComponent implements OnInit {
   public listenIcon = WellKnownIconsEnum.BARGE_IN_LISTEN.icon;
   public whisperIcon = WellKnownIconsEnum.BARGE_IN_WHISPER.icon;
   public conferenceIcon = WellKnownIconsEnum.BARGE_IN_CONFERENCE.icon;
   public editIcon = faEdit;
   public mailingIcon = faEnvelope;
   public callIcon = faPhone;
   public OFFLINE = UserStatusEnum.OFFLINE.value;
   public noEntrySelectedIcon = faUserSlash;

   @Select(DirectoryHelperState.getSelectedBrickInstanceId)
   public selectedDirectoryBrickInstanceId$: Observable<string>;

   @Select(DirectoryHelperState.getSelectedEntryID)
   public directoryId$: Observable<number>;

   @Select(ClientPhoneState.canMakeCallForNumber)
   public canMakeCallForNumber$: Observable<boolean>;

   @Select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA))
   public emailFeatureEnabled$: Observable<boolean>;

   public directoryInfo$: Observable<DirectoryEntryRemoteDto>;
   public defaultCallableColumn$: Observable<DirectoryColumn>;
   public availableColumns$: Observable<DirectoryColumn[]>;
   public visibleColumns$: Observable<string[]>;
   public visibleFields = [];

   public showAvatar$: Observable<boolean>;
   public showFieldNames$: Observable<boolean>;
   public callableColumns$: Observable<string[]>;
   public callableContent$: Observable<string | number | boolean | Date>;
   public searchableColumns$: Observable<string[]>;

   constructor(private store: Store) {
      super();
   }

   ngOnInit(): void {
      this.callableColumns$ = combineLatest([this.selectedDirectoryBrickInstanceId$]).pipe(
         switchMap(([directoryBrickinstanceId]) =>
            this.store.select(DirectorySettingsState.getAvailableCallColumnKeys).pipe(map((x) => x(directoryBrickinstanceId)))
         )
      );

      this.visibleColumns$ = this.store.select(DirectoryDetailsSettingState.getVisibleFields).pipe(map((fn) => fn(this.brickInstanceId)));

      this.directoryInfo$ = combineLatest([this.selectedDirectoryBrickInstanceId$, this.directoryId$]).pipe(
         switchMap(([brickinstanceId, id]) =>
            this.store.select(DirectoryDataBehaviourState.getDirectoryEntryById).pipe(map((getEntry) => getEntry(brickinstanceId, id)))
         )
      );

      this.callableContent$ = combineLatest([
         this.selectedDirectoryBrickInstanceId$,
         this.directoryInfo$,
         this.store.select(DirectorySettingsState.getDefaultOrAvailableCallColumn),
      ]).pipe(
         map(([directoryBrickInstanceId, directoryInfo, getColumn]) => getColumn(directoryBrickInstanceId, directoryInfo)),
         map((entry) => {
            if (entry?.isValueSet) {
               return entry.value;
            }
            return '';
         })
      );

      const extraColumns$: DirectoryColumn[] = [];
      extraColumns$.push(new DirectoryColumn('APPOINTMENTS', '', 'APPOINTMENTS'));
      extraColumns$.push(new DirectoryColumn('PRESENCE_STATUS', '', 'PRESENCE_STATUS'));
      extraColumns$.push(new DirectoryColumn('PRESENCE_STATUS_FORWARD', '', 'PRESENCE_STATUS_FORWARD'));
      extraColumns$.push(new DirectoryColumn('PRESENCE_STATUS_NOTE', '', 'PRESENCE_STATUS_NOTE'));
      extraColumns$.push(new DirectoryColumn('MOBILE_STATUS', '', 'MOBILE_STATUS'));
      extraColumns$.push(new DirectoryColumn('LINE_STATUS', '', 'LINE_STATUS'));

      const ordered$ = this.store.select(DirectoryStructureState.availableDirectoryColumns).pipe(
         map((f: DirectoryColumn[]) => {
            f.filter((dir) => dir.directoryColumnKey !== 'tbl_org_00_fld_orgnum' && dir.directoryColumnKey !== 'tbl_org_00_fld_orgnam');
            return _(f).concat(extraColumns$).value();
         })
      );

      this.availableColumns$ = combineLatest([this.visibleColumns$, ordered$]).pipe(
         map(([visibleColumns, availableColumns]) => {
            return _(availableColumns)
               .filter((f) => visibleColumns.indexOf(f.directoryColumnKey) > -1)
               .orderBy((f) => visibleColumns.indexOf(f.directoryColumnKey))
               .value();
         })
      );

      this.searchableColumns$ = this.selectedDirectoryBrickInstanceId$.pipe(
         switchMap((directoryBrickinstanceId) =>
            this.store
               .select(DirectorySettingsState.getDirectorySearchableColumns)
               .pipe(map((getDirectorySearchableColumns) => _.map(getDirectorySearchableColumns(directoryBrickinstanceId), (col) => col.directoryColumnKey)))
         )
      );

      this.showAvatar$ = this.store.select(DirectoryDetailsSettingState.getShowAvatar).pipe(map((fn) => fn(this.brickInstanceId)));
      this.showFieldNames$ = this.store.select(DirectoryDetailsSettingState.getShowFieldNames).pipe(map((fn) => fn(this.brickInstanceId)));
   }

   editEntry(brickInstanceId: string) {
      this.store.dispatch(new DirectoryBrowserEditEntry(brickInstanceId));
   }

   call(column: DirectoryColumn, entry: DirectoryEntryRemoteDto) {
      const numberToCall = getEntryValueForColumn(this.store, entry, column);

      this.store.dispatch(new StartNewCallAction(numberToCall.toString()));
   }

   callContent(callableContent: string) {
      if (callableContent.trim().length > 0) {
         this.store.dispatch(new StartNewCallAction(callableContent));
      }
   }

   composeEmail() {
      if (this.store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA))) {
         this.store.dispatch(new StartEmailProcess(EmailPurpose.COMPOSE_EMAIL));
      }
   }

   fireDirectorySearch(column: DirectoryColumn, entry: DirectoryEntryRemoteDto, id: string) {
      const searchValue = getEntryValueForColumn(this.store, entry, column);
      this.store.dispatch(new DirectorySearchInGivenColumns(id, searchValue, [column]));
   }
}
